import {
  ApiRoutes,
  CODIGO_UNIDADE_ATENDIMENTO,
  Mensagens,
  MotivoContato,
  TipoPessoa,
  TIPOS_PESSOA,
} from "../../models/types";
import {
  formatarData,
  getBaseURL,
  getDescricaoAdicionalProtocolo,
  getMatriculaResponsavelProtocolo,
  getMatriculaSolicitante,
  getNumeroDDD,
  getNumeroSemDDD,
  getOnlyNumbers,
  isApenasNumeros,
  isCnpjValido,
  isCpfValido,
} from "../../utils/functions";
import CardReCaptcha, {
  CardReCaptchaRef,
} from "../../components/form/CardReCaptcha";
import { z } from "zod";
import { useIonLoading } from "@ionic/react";
import { classNames } from "primereact/utils";
import React, { useEffect, useState } from "react";
import Page from "../../components/Page";
import Accordion from "../../components/Accordion";
import useNotification from "../../hooks/useNotification";
import ReactForm from "../../components/form/ReactForm";
import InputText from "../../components/form/InputText";
import SubmitButton from "../../components/form/SubmitButton";
import ButtonBack from "../../components/ButtonBack";
import SelectField from "../../components/form/SelectField";
import DivFaltaAgua from "../../components/DivFaltaAgua";
import axios from "axios";

export const TIPOS_TEMPO_FALTA_AGUA = (() => {
  const lista = ["06 horas", "12 horas", "18 horas", "24 horas", "+24 horas"];

  return lista.map((item) => {
    return { value: item, label: item };
  });
})();

export interface ConsultaFaltaAgua {
  dddTelefoneContato: number;
  numeroTelefoneContato: number;
  codigoMotivoContato: MotivoContato;
  matriculaResponsavel: string;
  descricaoAdicional: string;
  nomeContato: string;
  numeroCpfCnpj?: string | null;
  tipoCliente?: any;
}

export default function ComunicarFaltaAguaNoEndereco() {
  const reCaptchaRef = React.createRef<CardReCaptchaRef>();
  const notification = useNotification();

  const [present, dismiss] = useIonLoading();
  const [isFluxoCadastro, setIsFluxoCadastro] = useState<boolean>(false);
  const [tipoCliente, setTipoCliente] = useState<string>();

  const [numeroConta, setNumeroConta] = useState("");
  const [digitoConta, setDigitoConta] = useState("");
  const [protocolo, setProtocolo] = useState<number>();
  const [dadosConsultaFaltaAgua, setDadosConsultaFaltaAgua] =
    useState<ConsultaFaltaAgua>();

  const isTipoClientePessoaFisica = (() => {
    return tipoCliente === TipoPessoa.PESSOA_FISICA;
  })();

  const handleChangeNumeroConta = (e: any) => {
    if (e.target.value === "" || isApenasNumeros(e.target.value)) {
      setNumeroConta(e.target.value);
    }
  };

  const handleChangeDigitoConta = (e: any) => {
    if (e.target.value === "" || isApenasNumeros(e.target.value)) {
      setDigitoConta(e.target.value);
    }
  };

  useEffect(() => {
    setIsFluxoCadastro(false);
  }, []);

  const formSchemaConsultaConta = z
    .object({
      numeroConta: z
        .string()
        .min(1, "Informe o número da conta.")
        .transform((val) => getOnlyNumbers(val)),
      digitoConta: z
        .string()
        .min(1, "Informe o dígito da conta.")
        .transform((val) => getOnlyNumbers(val)),
      nomeContato: z.string().min(1, "Informe o nome do interessado."),
      cpfCnpj: z.string(),
      tipoCliente: z.any().transform((input) => input?.value),
      celular: z
        .string()
        .min(11, "Informe um número de contato.")
        .transform((val) => Number(getOnlyNumbers(val))),
      recaptcha: z.string().min(1, Mensagens.RECAPTCHA_VAZIO),
    })
    .refine(
      (data) =>
        data.cpfCnpj
          ? data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? isCpfValido(data.cpfCnpj)
            : isCnpjValido(data.cpfCnpj)
          : true,
      (data) => ({
        message:
          data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? "CPF inválido."
            : "CNPJ inválido.",
        path: ["cpfCnpj"],
      })
    );

  type formTypeConsultaConta = z.infer<typeof formSchemaConsultaConta>;

  async function consultarDados(
    dados: formTypeConsultaConta,
    resetFields: any
  ) {
    await present({ message: Mensagens.CONSULTANDO });

    try {
      let body = {
        dadosProtocolo: {
          dddTelefoneContato: getNumeroDDD(dados.celular),
          numeroTelefoneContato: getNumeroSemDDD(dados.celular),
          codigoMotivoContato: MotivoContato.FALTA_DE_AGUA,
          matriculaResponsavel: getMatriculaResponsavelProtocolo(),
          descricaoAdicional: getDescricaoAdicionalProtocolo(),
          nomeContato: dados.nomeContato,
          numeroCpfCnpj: dados.cpfCnpj ? getOnlyNumbers(dados.cpfCnpj) : null,
          tipoCliente: dados.cpfCnpj ? dados.tipoCliente : "",
        },

        numeroConta: numeroConta,
        numeroMatricula: getMatriculaResponsavelProtocolo(),
      };

      const params = new URLSearchParams({
        "g-recaptcha-response": dados.recaptcha,
      });

      let url =
        getBaseURL(ApiRoutes.ECO_ATENDIMENTO_EXTERNO) +
        "/atendimentos/consultar/falta-de-agua";

      const response = await axios.post(url, JSON.stringify(body), {
        params: params,
        headers: {
          "content-type": "application/json",
        },
      });

      // Consta registro de corte no sistema.
      if (response && response.status === 206) {
        notification
          .showAlert({
            message: `Consta registro de corte em nosso sitema. Consulte os débitos em aberto.`,
            redirect: "/segundaVia",
            buttons: [{ text: "Consultar", role: "confirm" }],
          })
          .then(() => {
            resetFields();
          });

        return;
      }

      // Abastecimento pode estar comprometido por manobra.
      if (response && response.status === 200) {
        notification.showAlert({
          message: `Informamos que devido a obras de melhorias na rede de distribuição de água, 
          o seu abastecimento pode estar comprometido a partir do dia ${formatarData(
            response?.data?.dataHoraProgramada
          )}. A previsão de normalização acontecerá ao longo do dia  ${formatarData(
            response?.data?.dataHoraNormalizacao
          )}, de forma gradual. A Saneago  recomenda o uso de caixa de água e consumo consciente.`,
        });

        return;
      }

      // registro criado, protocolo retornado
      if (response && response.status === 201) {
        setProtocolo(response?.data);
        setIsFluxoCadastro(true);
        setDadosConsultaFaltaAgua(body.dadosProtocolo);

        return;
      }

      notification.showAlert({
        message: `Problemas ao consultar se existe corte ou manobra. Status de sucesso "${response.status}" não mapeado.`,
      });
    } catch (e: any) {
      let msg = "";

      if (e.response.status === 401) {
        msg = Mensagens.RECAPTCHA_ERRO;
      }

      await notification.showError({ message: msg, exception: e });
    } finally {
      dismiss();
      reCaptchaRef?.current?.clear();
    }
  }

  const formSchema = z.object({
    pontoReferencia: z.string().min(1, "Informe um ponto de referência."),
    tempoFaltaDeAgua: z
      .any()
      .refine((input) => input, "Selecione o tempo da falta de água.")
      .transform((input) => input.value),
    recaptcha: z.string().min(1, Mensagens.RECAPTCHA_VAZIO),
  });

  type formType = z.infer<typeof formSchema>;

  async function enviarDados(dados: formType, resetFields: any) {
    await present({ message: Mensagens.SALVANDO });

    try {
      let body = {
        matriculaSolicitante: getMatriculaSolicitante(),
        codigoUnidadeAtendimento: CODIGO_UNIDADE_ATENDIMENTO,
        cpfCnpj: dadosConsultaFaltaAgua?.numeroCpfCnpj || null,
        nomeContato: dadosConsultaFaltaAgua?.nomeContato || null,
        tipoCliente: dadosConsultaFaltaAgua?.tipoCliente || null,
        numeroConta: numeroConta + "-" + digitoConta,
        pontoReferencia: dados.pontoReferencia,
        dddTelefoneContato: dadosConsultaFaltaAgua?.dddTelefoneContato,
        numeroTelefoneContato: dadosConsultaFaltaAgua?.numeroTelefoneContato,
        tempoFaltaDeAgua: dados.tempoFaltaDeAgua,
      };

      let url =
        getBaseURL(ApiRoutes.ECO_ATENDIMENTO_EXTERNO) +
        `/atendimentos/${protocolo}/registrar/falta-de-agua`;

      const params = new URLSearchParams({
        "g-recaptcha-response": dados.recaptcha,
      });

      const resp = await axios.post(url, JSON.stringify(body), {
        params: params,
        headers: {
          "content-type": "application/json",
        },
      });

      notification.showProtocolo(resp.data).then(() => {
        resetFields();
        setIsFluxoCadastro(false);
      });

      //
    } catch (e: any) {
      let msg = "";

      if (e.response.status === 401) {
        msg = Mensagens.RECAPTCHA_ERRO;
      }

      if (e.response.status === 403) {
        msg = Mensagens.SOLICITACAO_ABERTA;
      }

      await notification.showError({ message: msg, exception: e });
    } finally {
      dismiss();
      reCaptchaRef?.current?.clear();
    }
  }

  return (
    <Page
      title="Comunicar falta de água"
      buttonType="back"
      showCardConta={false}
    >
      <div className="flex flex-col space-y-4">
        <Accordion title="Observações" open={true}>
          <DivFaltaAgua />
        </Accordion>

        <div className="card bg-opacity-70">
          <div className="card-title">Dados da ocorrência</div>

          <div className="items-center">
            <div className="flex flex-col space-y-4">
              <ReactForm
                schema={formSchemaConsultaConta}
                onError={(error, e) => {}}
                onSubmit={(data, resetFields) =>
                  consultarDados(data as formTypeConsultaConta, resetFields)
                }
              >
                <div
                  className={classNames("flex flex-col space-y-4", {
                    "pointer-events-none select-none opacity-70":
                      isFluxoCadastro,
                  })}
                >
                  <div className="grid grid-cols-2 gap-4 xs:grid-cols-1">
                    <InputText
                      name="numeroConta"
                      label="Número da conta"
                      onInput={(e) => handleChangeNumeroConta(e)}
                      value={numeroConta}
                      inputProps={{ maxLength: 8 }}
                      disabled={isFluxoCadastro}
                    />

                    <InputText
                      name="digitoConta"
                      label="Dígito da conta"
                      onInput={(e) => handleChangeDigitoConta(e)}
                      value={digitoConta}
                      inputProps={{ maxLength: 1 }}
                      disabled={isFluxoCadastro}
                    />
                  </div>

                  <InputText
                    name="nomeContato"
                    label="Nome do interessado"
                    inputProps={{ maxLength: 70 }}
                  />

                  <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-3 gap-4">
                    <SelectField
                      name="tipoCliente"
                      placeHolder="Tipo de pessoa"
                      defaultValuePosition={0}
                      onSelect={(item) => {
                        setTipoCliente(item?.value as string);
                      }}
                      items={TIPOS_PESSOA}
                    />

                    <InputText
                      name="cpfCnpj"
                      label={
                        isTipoClientePessoaFisica
                          ? "CPF do interessado"
                          : "CNPJ do interessado"
                      }
                      mask={
                        isTipoClientePessoaFisica
                          ? "999.999.999-99"
                          : "99.999.999/9999-99"
                      }
                    />

                    <InputText
                      name="celular"
                      label="Celular"
                      mask="99 99999-9999"
                    />
                  </div>

                  <div hidden={isFluxoCadastro}>
                    <CardReCaptcha name="recaptcha" ref={reCaptchaRef} />
                  </div>

                  <div
                    className="grid grid-cols-2 gap-2 xs:grid-cols-1"
                    hidden={isFluxoCadastro}
                  >
                    <SubmitButton label="Consultar" />
                    <ButtonBack label="Voltar" />
                  </div>
                </div>
              </ReactForm>

              <ReactForm
                schema={formSchema}
                onError={(error, e) => {}}
                onSubmit={(data, resetFields) =>
                  enviarDados(data as formType, resetFields)
                }
              >
                <div
                  className="flex flex-col space-y-4"
                  hidden={!isFluxoCadastro}
                >
                  <InputText
                    name="pontoReferencia"
                    label="Ponto de referência"
                    inputProps={{ maxLength: 50 }}
                  />

                  <SelectField
                    name="tempoFaltaDeAgua"
                    placeHolder="Tempo de falta de água"
                    items={TIPOS_TEMPO_FALTA_AGUA}
                  />

                  <CardReCaptcha name="recaptcha" ref={reCaptchaRef} />

                  <div className="grid grid-cols-2 gap-2 xs:grid-cols-1">
                    <SubmitButton label="Enviar" />
                    <ButtonBack
                      label="Voltar"
                      onClickEvent={() => setIsFluxoCadastro(false)}
                    />
                  </div>
                </div>
              </ReactForm>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}
